// ScheduleBoard — the Schedule automation, styled as a Google Calendar twin:
// near-fullscreen box, left sidebar (mini month + "My team" checkbox list like
// GCal's My calendars + Needs-a-date), week grid that FITS the screen (no
// scrolling — 13 fixed hour rows share the height), today circled blue, red
// now-line, purple event blocks. Map toggle + per-job game-plan card retained.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../apiBase';

const API = getApiBase();
const DAY_START = 8, DAY_HOURS = 13; // 8a–8p fits one screen like his reference
const TEAM_COLORS = ['#7986cb', '#33b679', '#f4511e', '#8e24aa', '#e67c73', '#039be5', '#f6bf26'];

function money(n) { return n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function weekStart(base) { const d = new Date(base); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtTime(d) { let h = d.getHours(); const m = d.getMinutes(); const ap = h >= 12 ? 'pm' : 'am'; h = h % 12 || 12; return m ? `${h}:${String(m).padStart(2, '0')}${ap}` : `${h}${ap}`; }

export default function ScheduleBoard({ ownerId, onClose, onOpenSetup }) {
  const light = typeof document !== 'undefined' && !!document.querySelector('.theme-light, .ceo.theme-light');
  const C = light
    ? { bg: '#fff', ink: '#3c4043', sub: '#70757a', line: '#dde3ea', side: '#fff', hover: '#f1f3f4', box: '0 4px 28px rgba(32,33,36,.18)', chipBg: '#f1f3f4' }
    : { bg: '#131722', ink: '#e8eaed', sub: '#9aa0a6', line: 'rgba(255,255,255,.12)', side: '#131722', hover: 'rgba(255,255,255,.06)', box: '0 4px 28px rgba(0,0,0,.5)', chipBg: 'rgba(255,255,255,.08)' };

  const [team, setTeam] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pay, setPay] = useState({});
  const [know, setKnow] = useState([]);
  const [checked, setChecked] = useState(null);   // Set of memberIds (null = all)
  const [mode, setMode] = useState('cal');
  const [gcalSrc, setGcalSrc] = useState(() => { try { return localStorage.getItem('aria_gcal_embed_src') || ''; } catch (_) { return ''; } });
  const knownEmail = (() => { try { return localStorage.getItem('aria_session_email') || localStorage.getItem('aria_user_email') || ''; } catch (_) { return ''; } })();
  const [anchor, setAnchor] = useState(new Date());
  const [mini, setMini] = useState(new Date());
  const [sel, setSel] = useState(null);
  const [now, setNow] = useState(new Date());
  // New Job form — the owner's "I booked a job" hand-off to Aria. On save it hits
  // POST /api/jobs → lifecycle.createJob, which files the job, texts + emails the
  // owner the details, and (if a time is set) mirrors it to Google Calendar.
  const [showNew, setShowNew] = useState(false);
  const [nj, setNj] = useState({ description: '', name: '', address: '', phone: '', email: '', when: '' });
  const [njBusy, setNjBusy] = useState(false);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const submitNewJob = async () => {
    if (njBusy) return;
    const description = nj.description.trim();
    const name = nj.name.trim();
    if (!description && !name) { alert('Add at least what the job is, or the customer name.'); return; }
    setNjBusy(true);
    try {
      const body = {
        ownerId,
        description,
        address: nj.address.trim() || undefined,
        customer: { name: name || undefined, phone: nj.phone.trim() || undefined, email: nj.email.trim() || undefined },
        scheduledAt: nj.when ? new Date(nj.when).toISOString() : undefined,
        source: 'owner_new_job',
      };
      const r = await fetch(`${API}/api/jobs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((x) => x.json()).catch(() => null);
      if (r && r.ok) {
        setShowNew(false);
        setNj({ description: '', name: '', address: '', phone: '', email: '', when: '' });
        load();
      } else {
        alert('Could not save the job — ' + ((r && r.error) || 'try again'));
      }
    } finally { setNjBusy(false); }
  };

  const load = async () => {
    try {
      const q = `ownerId=${encodeURIComponent(ownerId)}`;
      const [t, j, p, k] = await Promise.all([
        fetch(`${API}/api/drawers/schedule/team?${q}`).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/jobs?${q}`).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/drawers/payments/paychecks?${q}&period=month`).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/drawers/schedule/jobknowledge?${q}`).then((r) => r.json()).catch(() => null),
      ]);
      setTeam((t && t.team) || []);
      setJobs((j && j.jobs) || []);
      const byId = {}; for (const pc of (p && p.paychecks) || []) byId[pc.memberId || pc.id] = pc;
      setPay(byId);
      setKnow((k && k.jobTypes) || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, [ownerId]);

  const start = weekStart(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const crewId = (j) => (j.crew && /#(\d+)$/.test(j.crew) ? Number(j.crew.match(/#(\d+)$/)[1]) : null);
  // Owner's booking colors: green confirmed · yellow set · red canceled/reschedule
  const statusColor = (j) => {
    const st = String(j.status || '').toLowerCase();
    if (/confirm|paid|complete|invoiced/.test(st)) return '#188038';
    if (/cancel|problem|reschedul/.test(st)) return '#d93025';
    return '#f4b400';
  };
  const colorOf = (j) => { const id = crewId(j); const idx = team.findIndex((m) => m.id === id); return TEAM_COLORS[idx >= 0 ? idx % TEAM_COLORS.length : 0]; };
  const visible = useMemo(() => jobs.filter((j) => (checked == null ? true : checked.has(crewId(j)))), [jobs, checked, team]);
  const scheduled = visible.filter((j) => j.scheduled_at);
  const unscheduled = visible.filter((j) => !j.scheduled_at && !j.completed_at);
  const est = (job) => { const key = (job.trade || job.title || '').slice(0, 24); const hit = know.find((k) => k.jobType && key && k.jobType.slice(0, 24) === key); return hit && hit.estHours; };
  const materials = (job) => { const m = String(job.description || '').match(/materials?[:\-]\s*([^·\n]+)/i); return m ? m[1].trim() : null; };
  const addr = (job) => job.address || (String(job.description || '').match(/Address:\s*([^·\n]+)/i) || [])[1] || null;
  const toggle = (id) => setChecked((c) => { const all = new Set(team.map((m) => m.id)); all.add(null); const cur = c == null ? all : new Set(c); cur.has(id) ? cur.delete(id) : cur.add(id); return cur; });
  const mapQ = sel ? (addr(sel) || 'Wichita KS') : (scheduled.map(addr).find(Boolean) || 'Wichita KS');

  // mini month cells
  const mFirst = new Date(mini.getFullYear(), mini.getMonth(), 1);
  const mCells = Array.from({ length: 42 }, (_, i) => addDays(weekStart(mFirst), i));

  const btn = { background: 'transparent', color: C.ink, border: `1px solid ${C.line}`, borderRadius: 20, padding: '7px 15px', cursor: 'pointer', fontSize: 13.5, fontWeight: 500 };
  const nowTop = ((now.getHours() + now.getMinutes() / 60) - DAY_START) / DAY_HOURS * 100;

  return (
    // WebkitAppRegion no-drag: the board's top bar (← Back, Today, view tabs) sits inside the
    // frameless window's 52px titlebar drag band. Electron drag regions ignore z-index, so
    // without this the first click on those buttons DRAGGED the window instead of clicking.
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: light ? 'rgba(32,33,36,.45)' : 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitAppRegion: 'no-drag' }}>
      <div style={{ width: 'calc(100vw - 28px)', height: 'calc(100vh - 24px)', background: C.bg, color: C.ink, borderRadius: 16, boxShadow: C.box, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'inherit' }}>

        {/* ── top bar (GCal) ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderBottom: `1px solid ${C.line}`, flex: '0 0 auto' }}>
          <button style={btn} onClick={onClose}>← Back</button>
          <strong style={{ fontSize: 17, fontWeight: 600 }}>Schedule</strong>
          <button style={{ ...btn, background: '#1a73e8', color: '#fff', border: '1px solid #1a73e8', fontWeight: 600 }} onClick={() => setShowNew(true)}>+ New Job</button>
          <button style={btn} onClick={() => { setAnchor(new Date()); setMini(new Date()); }}>Today</button>
          <span style={{ display: 'flex', gap: 2 }}>
            <button style={{ ...btn, border: 'none', padding: '7px 9px', fontSize: 16 }} onClick={() => setAnchor(addDays(anchor, -7))}>‹</button>
            <button style={{ ...btn, border: 'none', padding: '7px 9px', fontSize: 16 }} onClick={() => setAnchor(addDays(anchor, 7))}>›</button>
          </span>
          <span style={{ fontSize: 20, fontWeight: 400 }}>{start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
          <div style={{ flex: 1 }} />
          <span style={{ display: 'flex', gap: 10, fontSize: 11.5, color: C.sub, alignItems: 'center' }}>
          {[['#188038', 'Confirmed'], ['#f4b400', 'Set'], ['#d93025', 'Canceled / reschedule']].map(([c, l]) => (
            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />{l}
            </span>
          ))}
        </span>
        {[['google', 'Google Calendar'], ['cal', 'Week'], ['map', 'Map']].map(([k, lbl]) => (
            <button key={k} style={{ ...btn, ...(mode === k ? { background: '#1a73e8', color: '#fff', border: '1px solid #1a73e8' } : {}) }} onClick={() => setMode(k)}>{lbl}</button>
          ))}
          {onOpenSetup && <button style={btn} onClick={onOpenSetup}>Google Calendar setup</button>}
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* ── sidebar: mini month + My team + Needs a date ── */}
          <div style={{ width: 236, flex: '0 0 auto', borderRight: `1px solid ${C.line}`, padding: '12px 14px', overflowY: 'auto', background: C.side }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13.5, fontWeight: 600, padding: '2px 4px' }}>
              <span>{mini.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
              <span>
                <button style={{ ...btn, border: 'none', padding: '2px 7px' }} onClick={() => setMini(new Date(mini.getFullYear(), mini.getMonth() - 1, 1))}>‹</button>
                <button style={{ ...btn, border: 'none', padding: '2px 7px' }} onClick={() => setMini(new Date(mini.getFullYear(), mini.getMonth() + 1, 1))}>›</button>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', textAlign: 'center', fontSize: 10.5, color: C.sub, marginTop: 6 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} style={{ padding: 3 }}>{d}</div>)}
              {mCells.map((d, i) => {
                const today = d.toDateString() === new Date().toDateString();
                const inWeek = d >= start && d < addDays(start, 7);
                return (
                  <div key={i}
                    style={{ padding: 4, cursor: 'pointer', borderRadius: '50%', color: d.getMonth() !== mini.getMonth() ? (light ? '#b8bcc2' : '#5f6368') : C.ink,
                      background: today ? '#1a73e8' : inWeek ? (light ? '#d2e3fc' : 'rgba(26,115,232,.35)') : 'transparent',
                      ...(today ? { color: '#fff', fontWeight: 700 } : {}) }}
                    onClick={() => setAnchor(new Date(d))}>{d.getDate()}</div>
                );
              })}
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, letterSpacing: 0.8, margin: '16px 4px 6px' }}>MY TEAM</div>
            {team.map((m, i) => {
              const on = checked == null || checked.has(m.id);
              const p = pay[m.id] || {};
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 4px', borderRadius: 8, cursor: 'pointer' }} onClick={() => toggle(m.id)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ width: 14, height: 14, marginTop: 2, borderRadius: 3, flex: '0 0 auto', background: on ? TEAM_COLORS[i % TEAM_COLORS.length] : 'transparent', border: `2px solid ${TEAM_COLORS[i % TEAM_COLORS.length]}` }} />
                  <span style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: C.sub }}>in {money(Number(p.revenue) || 0)} · pay {money(Number(p.pay ?? p.amount) || 0)}</div>
                  </span>
                </div>
              );
            })}
            {team.length === 0 && <div style={{ fontSize: 12.5, color: C.sub, padding: '2px 4px' }}>No team yet — tell Aria "add a worker" and she'll set them up with pay + calendar.</div>}

            {unscheduled.length > 0 && (<>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, letterSpacing: 0.8, margin: '16px 4px 6px' }}>NEEDS A DATE</div>
              {unscheduled.slice(0, 6).map((j) => (
                <div key={j.id} style={{ padding: '6px 8px', borderRadius: 8, cursor: 'pointer', borderLeft: '3px solid #f4b400', background: C.hover, marginBottom: 5, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => setSel(j)}>
                  {j.title || 'Job #' + j.id}
                </div>
              ))}
            </>)}
          </div>

          {/* ── main: week grid OR map ── */}
          {mode === 'google' ? (
            <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
              {(typeof window !== 'undefined' && window.electronAPI) ? (
                // Desktop: the REAL Google Calendar app — fully interactive
                // (click, create, drag). Chrome UA so Google allows sign-in;
                // persist: partition keeps the login across restarts. The
                // owner signs in themselves the first time.
                <webview
                  src={knownEmail
                    ? 'https://accounts.google.com/AccountChooser?Email=' + encodeURIComponent(knownEmail) + '&continue=https%3A%2F%2Fcalendar.google.com%2Fcalendar%2Fu%2F0%2Fr%2Fweek'
                    : 'https://accounts.google.com/ServiceLogin?service=cl&continue=https%3A%2F%2Fcalendar.google.com%2Fcalendar%2Fu%2F0%2Fr%2Fweek'}
                  partition="persist:gcal"
                  useragent="Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:127.0) Gecko/20100101 Firefox/127.0"
                 
                  style={{ width: '100%', height: '100%', border: 0, background: light ? '#fff' : '#131722', display: 'inline-flex' }}
                />
              ) : gcalSrc ? (
                <iframe title="google calendar" style={{ width: '100%', height: '100%', border: 0, background: light ? '#fff' : '#131722' }}
                  src={'https://calendar.google.com/calendar/embed?mode=WEEK&wkst=1&showPrint=0&showCalendars=0&ctz=' + encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago') + '&bgcolor=' + encodeURIComponent(light ? '#ffffff' : '#131722') + '&src=' + encodeURIComponent(gcalSrc)} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, padding: 30, textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Show your real Google Calendar here</div>
                  <div style={{ fontSize: 13, color: C.sub, maxWidth: 420 }}>Enter the Google account email whose calendar to show. Heads up: Google only renders it in this box if the calendar is shared or you're signed in — if it comes up blank, hit "Google Calendar setup" and Aria walks you through sharing it.</div>
                  {knownEmail && (
                    <button style={{ ...btn, borderRadius: 20, background: '#e8f0fe', color: '#1a73e8', border: '1px solid #1a73e8', fontWeight: 600 }}
                      onClick={() => { try { localStorage.setItem('aria_gcal_embed_src', knownEmail); } catch (_) {} setGcalSrc(knownEmail); }}>
                      Is this your email? {knownEmail} — use it
                    </button>
                  )}
                  <input placeholder="you@gmail.com" style={{ ...btn, borderRadius: 8, width: 280, padding: 10 }} onKeyDown={(e) => {
                    if (e.key === 'Enter') { const v = e.target.value.trim(); if (v) { try { localStorage.setItem('aria_gcal_embed_src', v); } catch (_) {} setGcalSrc(v); } }
                  }} />
                  <div style={{ fontSize: 11.5, color: C.sub }}>press Enter — the Week view next door always shows your jobs either way</div>
                </div>
              )}
            </div>
          ) : mode === 'cal' ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {/* day headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(7,1fr)', borderBottom: `1px solid ${C.line}`, flex: '0 0 auto' }}>
                <div style={{ fontSize: 9.5, color: C.sub, alignSelf: 'end', padding: '0 4px 4px' }}>GMT-05</div>
                {days.map((d, i) => {
                  const today = d.toDateString() === new Date().toDateString();
                  return (
                    <div key={i} style={{ textAlign: 'center', padding: '6px 0 8px' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, color: today ? '#1a73e8' : C.sub }}>{d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}</div>
                      <div style={{ width: 40, height: 40, lineHeight: '40px', margin: '2px auto 0', borderRadius: '50%', fontSize: 22, fontWeight: 400, background: today ? '#1a73e8' : 'transparent', color: today ? '#fff' : C.ink }}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
              {/* hour grid — fills remaining height, NO scroll */}
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '52px repeat(7,1fr)', gridTemplateRows: `repeat(${DAY_HOURS},1fr)`, minHeight: 0, position: 'relative' }}>
                {Array.from({ length: DAY_HOURS }, (_, r) => {
                  const h = DAY_START + r;
                  const lbl = h === 12 ? '12 PM' : h > 12 ? (h - 12) + ' PM' : h + ' AM';
                  return (
                    <React.Fragment key={r}>
                      <div style={{ gridRow: r + 1, gridColumn: 1, fontSize: 9.5, color: C.sub, textAlign: 'right', paddingRight: 6, transform: 'translateY(-6px)', borderTop: r ? `1px solid ${C.line}` : 'none' }}>{r ? lbl : ''}</div>
                      {days.map((d, c) => {
                        const today = d.toDateString() === new Date().toDateString();
                        const cellJobs = scheduled.filter((j) => { const t = new Date(j.scheduled_at); return t.toDateString() === d.toDateString() && t.getHours() === h; });
                        return (
                          <div key={c} style={{ gridRow: r + 1, gridColumn: c + 2, borderTop: r ? `1px solid ${C.line}` : 'none', borderLeft: `1px solid ${C.line}`, position: 'relative' }}>
                            {cellJobs.map((j) => {
                              const t = new Date(j.scheduled_at);
                              const durH = Math.max(1, Math.min(4, Math.round(est(j) || 1)));
                              const end = new Date(t.getTime() + durH * 3600000);
                              return (
                                <div key={j.id} onClick={() => setSel(j)} title={j.title}
                                  style={{ position: 'absolute', top: `${(t.getMinutes() / 60) * 100}%`, left: 2, right: 4, height: `calc(${durH * 100}% - 4px)`, zIndex: 1,
                                    background: statusColor(j), borderLeft: '4px solid ' + colorOf(j), color: '#fff', borderRadius: 6, padding: '4px 7px', fontSize: 11.5, cursor: 'pointer', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.25)' }}>
                                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.title || 'Job #' + j.id}</div>
                                  <div style={{ opacity: 0.9 }}>{fmtTime(t)} – {fmtTime(end)}</div>
                                </div>
                              );
                            })}
                            {today && nowTop >= (r / DAY_HOURS) * 100 && nowTop < ((r + 1) / DAY_HOURS) * 100 && (
                              <div style={{ position: 'absolute', left: 0, right: 0, top: `${(nowTop - (r / DAY_HOURS) * 100) * DAY_HOURS}%`, zIndex: 2, pointerEvents: 'none' }}>
                                <div style={{ height: 2, background: '#ea4335' }} />
                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ea4335', marginTop: -6, marginLeft: -5 }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, position: 'relative' }}>
              <iframe title="job map" src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQ)}&z=11&output=embed`} style={{ width: '100%', height: '100%', border: 0 }} />
              <div style={{ position: 'absolute', top: 10, left: 10, background: C.bg, color: C.ink, borderRadius: 10, padding: '8px 12px', fontSize: 12.5, maxWidth: 300, boxShadow: C.box }}>
                {sel ? <>Showing: <strong>{sel.title || 'Job #' + sel.id}</strong></> : 'Click a job to pin it on the map.'}
                {scheduled.slice(0, 6).map((j) => (
                  <div key={j.id} style={{ cursor: 'pointer', padding: '4px 0', borderTop: `1px solid ${C.line}`, fontWeight: sel && sel.id === j.id ? 700 : 400 }} onClick={() => setSel(j)}>
                    {new Date(j.scheduled_at).toLocaleDateString(undefined, { weekday: 'short' })} — {j.title || 'Job #' + j.id}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── game plan card ── */}
          {sel && (
            <div style={{ width: 320, flex: '0 0 auto', borderLeft: `1px solid ${C.line}`, padding: 16, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 15 }}>Game plan</strong>
                <button style={btn} onClick={() => setSel(null)}>✕</button>
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, marginTop: 10 }}>{sel.title || 'Job #' + sel.id}</div>
              {sel.scheduled_at && <div style={{ fontSize: 12.5, color: C.sub }}>{new Date(sel.scheduled_at).toLocaleString()}</div>}
              {[['The job', (sel.description || 'No details yet.').slice(0, 480)],
                ['Time estimate', est(sel) ? `~${est(sel)} hours (from your history)` : 'No history yet — Aria learns it after the first few.'],
                ...(materials(sel) ? [['Materials', materials(sel)]] : []),
                ['Where', addr(sel) || 'No address on file.']].map(([h, v]) => (
                <div key={h}>
                  <div style={{ fontSize: 11, letterSpacing: 1, color: C.sub, textTransform: 'uppercase', margin: '13px 0 3px' }}>{h}</div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{v}</div>
                </div>
              ))}
              <div style={{ fontSize: 11, letterSpacing: 1, color: C.sub, textTransform: 'uppercase', margin: '13px 0 3px' }}>Crew</div>
              <select value={crewId(sel) || ''} style={{ ...btn, width: '100%', padding: 9, borderRadius: 8 }}
                onChange={async (e) => { const memberId = Number(e.target.value); if (!memberId) return;
                  await fetch(`${API}/api/drawers/schedule/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ownerId, jobId: sel.id, memberId }) }).catch(() => {});
                  load(); }}>
                <option value="">Unassigned — pick a worker</option>
                {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button style={{ ...btn, width: '100%', marginTop: 12, background: '#1a73e8', color: '#fff', border: '1px solid #1a73e8', borderRadius: 8 }}
                onClick={async () => {
                  const r = await fetch(`${API}/api/drawers/schedule/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ownerId, jobId: sel.id, dryRun: false }) }).then((x) => x.json()).catch(() => null);
                  alert(r && r.ok ? 'Confirmation texts sent.' : 'Could not send — ' + ((r && r.error) || 'try again'));
                }}>
                Send confirmation texts
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── New Job modal — the owner tells Aria about a job they booked ── */}
      {showNew && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitAppRegion: 'no-drag' }}
          onClick={() => { if (!njBusy) setShowNew(false); }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(460px, 92vw)', maxHeight: '92vh', overflowY: 'auto', background: C.bg, color: C.ink, borderRadius: 16, boxShadow: C.box, padding: '20px 22px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 18 }}>New job</strong>
              <button style={{ ...btn, border: 'none', fontSize: 18, padding: '2px 9px' }} onClick={() => { if (!njBusy) setShowNew(false); }}>✕</button>
            </div>
            <div style={{ fontSize: 12.5, color: C.sub, margin: '4px 0 16px' }}>Booked a job? Tell Aria — she files it, texts and emails you the details, and drops it on your calendar.</div>

            {[
              ["What's the job?", 'description', 'e.g. Garage cleanout — full haul'],
              ['Customer name', 'name', 'Jane Smith'],
              ['Address', 'address', '123 Main St, Wichita KS'],
              ['Phone', 'phone', '(316) 555-0123'],
              ['Email', 'email', 'jane@email.com'],
            ].map(([label, key, ph]) => (
              <label key={key} style={{ display: 'block', marginBottom: 12 }}>
                <div style={{ fontSize: 11, letterSpacing: 0.6, color: C.sub, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                <input value={nj[key]} placeholder={ph} onChange={(e) => setNj((s) => ({ ...s, [key]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && key !== 'address') submitNewJob(); }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.line}`, background: C.chipBg, color: C.ink, fontSize: 14, boxSizing: 'border-box' }} />
              </label>
            ))}

            <label style={{ display: 'block', marginBottom: 18 }}>
              <div style={{ fontSize: 11, letterSpacing: 0.6, color: C.sub, textTransform: 'uppercase', marginBottom: 4 }}>When <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></div>
              <input type="datetime-local" value={nj.when} onChange={(e) => setNj((s) => ({ ...s, when: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.line}`, background: C.chipBg, color: C.ink, fontSize: 14, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>Leave it blank and the job lands in “Needs a date.”</div>
            </label>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={btn} onClick={() => { if (!njBusy) setShowNew(false); }}>Cancel</button>
              <button style={{ ...btn, background: '#1a73e8', color: '#fff', border: '1px solid #1a73e8', fontWeight: 600, opacity: njBusy ? 0.6 : 1 }} disabled={njBusy} onClick={submitNewJob}>
                {njBusy ? 'Saving…' : 'Save job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
